import { post } from './api'

/**
 * The iOS shell, seen from the web app.
 *
 * The app on the App Store is a WKWebView on https://adulting.studio42.app
 * (capacitor.config.ts). Capacitor injects `window.Capacitor` into that page,
 * and the native plugins are reached through it — NOT by importing
 * `@capacitor/push-notifications`, because the web bundle should not carry a
 * native plugin's code, and because every check here has to work on:
 *
 *   - a plain browser, where there is no Capacitor at all;
 *   - an installed OLDER shell that lacks a plugin this code would like.
 *
 * So everything is feature-detected with `isPluginAvailable`, and the answer
 * when something is missing is "not here", never a thrown error. A web deploy
 * reaches every installed phone at once, and must never assume a native
 * capability the installed binary does not have.
 */

interface Handle { remove(): Promise<void> }

interface PushPlugin {
  checkPermissions(): Promise<{ receive: string }>
  requestPermissions(): Promise<{ receive: string }>
  register(): Promise<void>
  // Depending on the bridge, this returns a Promise<Handle> or the Handle
  // itself. Never chain on it directly; go through listen() below.
  addListener(event: 'registration', fn: (token: { value: string }) => void): Promise<Handle> | Handle
  addListener(event: 'registrationError', fn: (err: { error?: string }) => void): Promise<Handle> | Handle
  addListener(
    event: 'pushNotificationActionPerformed',
    fn: (action: { notification?: { data?: Record<string, unknown> } }) => void,
  ): Promise<Handle> | Handle
}

interface HapticsPlugin {
  notification(options: { type: 'SUCCESS' | 'WARNING' | 'ERROR' }): Promise<void>
  impact(options: { style: 'LIGHT' | 'MEDIUM' | 'HEAVY' }): Promise<void>
}

interface CapacitorGlobal {
  isNativePlatform?: () => boolean
  getPlatform?: () => string
  isPluginAvailable?: (name: string) => boolean
  Plugins?: Record<string, unknown>
}

function cap(): CapacitorGlobal | null {
  return (globalThis as { Capacitor?: CapacitorGlobal }).Capacitor ?? null
}

/** Inside our own iPhone app, rather than Safari or any other browser. */
export function inIosShell(): boolean {
  const c = cap()
  return Boolean(c?.isNativePlatform?.()) && c?.getPlatform?.() === 'ios'
}

function plugin<T>(name: string): T | null {
  const c = cap()
  if (!inIosShell() || !c?.isPluginAvailable?.(name)) return null
  return (c.Plugins?.[name] as T | undefined) ?? null
}

/** The installed shell can ask iOS for an APNs token. */
export function nativePushAvailable(): boolean {
  return plugin<PushPlugin>('PushNotifications') !== null
}

/** Nothing here may hang for ever: on a phone with no network, Apple simply never answers. */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`Timed out: ${label}`)), ms)),
  ])
}

export type NativePushState = 'unsupported' | 'blocked' | 'off' | 'on'

/** What the server called this phone's APNs row. Separate from the web key on purpose. */
const NATIVE_SUB_KEY = 'nativeSubscriptionId'

export async function nativePushState(): Promise<NativePushState> {
  const push = plugin<PushPlugin>('PushNotifications')
  if (!push) return 'unsupported'
  const { receive } = await push.checkPermissions()
  if (receive === 'denied') return 'blocked'
  return receive === 'granted' && localStorage.getItem(NATIVE_SUB_KEY) ? 'on' : 'off'
}

/**
 * The token arrives through an EVENT, not a promise: `register()` resolves as
 * soon as iOS has been asked, before Apple has answered. Awaiting only
 * `register()` looks like it works and never sends a token. So the listeners
 * go on first, and are removed afterwards so a second tap does not stack them.
 */
async function token(push: PushPlugin): Promise<string> {
  const handles: Promise<Handle | undefined>[] = []
  try {
    return await withTimeout(new Promise<string>((resolve, reject) => {
      handles.push(listen(() => push.addListener('registration', (t) => resolve(t.value))))
      handles.push(listen(() => push.addListener('registrationError', (e) => reject(new Error(e?.error || 'Apple refused the registration.')))))
      push.register().catch(reject)
    }), 30_000, 'Apple did not answer')
  } finally {
    for (const h of handles) void h.then((x) => x?.remove()).catch(() => {})
  }
}

/**
 * addListener, made safe.
 *
 * The page is loaded from the web into the shell, so it talks to the raw
 * window.Capacitor bridge rather than @capacitor/core. On that bridge
 * addListener returns the handle itself, not a promise, and chaining .catch on
 * it threw at startup: TestFlight build 1 showed only the background.
 * Whatever comes back (promise, handle, or a throw) becomes a promise that
 * never rejects.
 */
function listen(add: () => Promise<Handle> | Handle): Promise<Handle | undefined> {
  try {
    return Promise.resolve(add()).catch(() => undefined)
  } catch {
    return Promise.resolve(undefined)
  }
}

async function registerToken(push: PushPlugin, deviceId: string, label?: string): Promise<void> {
  const value = await token(push)
  // Same endpoint, same merge rules as a browser subscription: the server
  // stores the APNs token in push_subs.endpoint with platform 'ios', and
  // notify.ts routes that row to APNs instead of Web Push.
  const { subscriptionId } = await post<{ subscriptionId: string }>('/api/push/subscribe', {
    endpoint: value,
    platform: 'ios',
    deviceId,
    label: label ?? 'iPhone app',
  })
  localStorage.setItem(NATIVE_SUB_KEY, subscriptionId)
}

/**
 * Ask, then register. Call from a tap: the permission prompt is a one-time
 * question on iOS, and asking it unprompted wastes it.
 */
export async function enableNativePush(deviceId: string, label?: string): Promise<NativePushState> {
  const push = plugin<PushPlugin>('PushNotifications')
  if (!push) return 'unsupported'
  let { receive } = await push.checkPermissions()
  if (receive === 'prompt' || receive === 'prompt-with-rationale') {
    receive = (await withTimeout(push.requestPermissions(), 60_000, 'no answer to the permission prompt')).receive
  }
  if (receive !== 'granted') return receive === 'denied' ? 'blocked' : 'off'
  await registerToken(push, deviceId, label)
  return 'on'
}

/**
 * On launch, when permission was already given: register again, quietly.
 *
 * Apple rotates tokens (a restore, a reinstall), and the signed-in account on
 * this phone may have changed since the token was sent — subscribe() re-binds
 * the row to whoever is signed in now. Never prompts.
 */
export async function resyncNativePush(deviceId: string): Promise<void> {
  const push = plugin<PushPlugin>('PushNotifications')
  if (!push) return
  try {
    const { receive } = await push.checkPermissions()
    if (receive === 'granted') await registerToken(push, deviceId)
  } catch {
    // Offline, or Apple slow: the next launch tries again.
  }
}

/**
 * Open the screen a tapped notification points at.
 *
 * iOS hands the tap to the app, not to the page, so without this the app opens
 * wherever it was and the notification seems to do nothing. Only a same-origin
 * path is followed: a push must not be able to choose an arbitrary destination.
 */
export function listenForNativeTaps(navigate: (path: string) => void): void {
  const push = plugin<PushPlugin>('PushNotifications')
  if (!push) return
  void listen(() => push.addListener('pushNotificationActionPerformed', (action) => {
    const data = action?.notification?.data ?? {}
    const target = data.target
    if (typeof data.inboxId === 'string') void post('/api/push/ack', { ids: [data.inboxId] }).catch(() => {})
    if (typeof target === 'string' && target.startsWith('/') && !target.startsWith('//')) navigate(target)
  }))
}

/**
 * A native tap of the Taptic Engine. `navigator.vibrate` does not exist on
 * iOS, so without this a right answer is silent in the hand. Returns false when
 * the shell has no Haptics plugin, so the caller can fall back.
 */
export function nativeHaptic(kind: 'success' | 'warning' | 'light'): boolean {
  const haptics = plugin<HapticsPlugin>('Haptics')
  if (!haptics) return false
  const done = kind === 'light'
    ? haptics.impact({ style: 'LIGHT' })
    : haptics.notification({ type: kind === 'success' ? 'SUCCESS' : 'WARNING' })
  void done.catch(() => {})
  return true
}
